import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import QRCode from 'react-qr-code';
import styled, { useTheme } from 'styled-components';

import { Button, Tooltip } from '@column/column-ui-kit';

import { CodeInput, Loading } from '~/components';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { UserModel } from '~/models';
import { FactorType, MFAStatus, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Box, Grid } from '~/styles';
import { copyToClipboard } from '~/util';

const CodeInputDescription = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  text-wrap: pretty;
`;

const StyledTooltip = styled(Tooltip)<{ $fullWidth?: boolean }>`
  display: inline;
  width: ${({ $fullWidth }) => ($fullWidth ? 'auto' : '100%')};
`;

const VerificationOverview = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  font-size: 14px;
  justify-content: center;
  padding: ${({ $fullWidth }) => ($fullWidth ? '18px 24px' : '0')};
  width: 100%;

  ${({ $fullWidth, theme }) =>
    $fullWidth &&
    `
      border-bottom: 1px solid ${theme.secondary.blendToBackground(150)};
      box-sizing: border-box;
    `}

  p {
    max-width: 440px;
    margin: 0;
    text-align: center;
    color: ${({ theme }) => theme.secondary.background};
  }
`;

const MfaWrapper = styled.div<{ $fullWidth?: boolean }>`
  display: grid;
  grid-template-columns: auto;
  max-width: none;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: ${({ $fullWidth }) => ($fullWidth ? '180px minmax(0, 1fr)' : 'min-content minmax(0, 1fr)')};
  }
`;

const MfaQRCode = styled.div<{ $fullWidth?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 32px 24px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: ${({ $fullWidth }) => ($fullWidth ? '180px' : 'auto')};
    padding: ${({ $fullWidth }) => ($fullWidth ? '20px' : '24px')};
    border-right: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
    box-sizing: border-box;
  }
`;

const MfaInput = styled.div<{ $fullWidth?: boolean }>`
  padding: 24px;
  display: flex;
  flex-direction: column;

  ${({ $fullWidth }) =>
    $fullWidth
      ? `
      align-items: center;
      justify-content: center;
      min-width: 0;
    `
      : `
      align-items: center;
      text-align: center;
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    ${({ $fullWidth }) =>
      !$fullWidth &&
      `
        align-items: flex-start;
        text-align: left;
      `}
  }
`;

const MfaSetupCode = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  align-items: center;

  ${({ $fullWidth, theme }) =>
    $fullWidth &&
    `
      align-items: center;
      border-top: 1px solid ${theme.secondary.blendToBackground(150)};
      box-sizing: border-box;
      flex-direction: column;
      justify-content: space-between;
      margin-top: 0;
      min-height: 60px;
      padding: 12px 20px;
      width: 100%;
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: ${({ $fullWidth }) => ($fullWidth ? 'center' : 'start')};
    flex-direction: ${({ $fullWidth }) => ($fullWidth ? 'row' : 'column')};
  }

  p {
    margin: 0 0 2px;
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.secondary.blendToBackground(600)};
    max-width: ${({ $fullWidth }) => ($fullWidth ? '260px' : 'none')};
    text-align: ${({ $fullWidth }) => ($fullWidth ? 'left' : 'center')};

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      text-align: left;
    }
  }
`;

const StyledBox = styled(Box)<{ $fullWidth?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;

  ${({ $fullWidth }) =>
    $fullWidth &&
    `
      flex-direction: column;
      overflow: hidden;
      position: relative;
      width: 100%;

      > div {
        width: 100%;
      }
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

interface ActionInit {
  action: 'Init';
}

interface ActionStartFactorSetup {
  action: 'StartFactorSetup';
  user: UserModel;
}

interface ActionCompleteFactorSetup {
  action: 'CompleteFactorSetup';
  factorId: string;
  otpauthUri: string;
}

interface ActionStartFactorVerification {
  action: 'StartFactorVerification';
}

interface ActionFailFactorVerification {
  action: 'FailFactorVerification';
}

interface ActionCompleteSetup {
  action: 'CompleteSetup';
}

interface ActionError {
  action: 'Error';
  message: string;
}

type MfaAction =
  | ActionInit
  | ActionStartFactorSetup
  | ActionCompleteFactorSetup
  | ActionStartFactorVerification
  | ActionFailFactorVerification
  | ActionCompleteSetup
  | ActionError;

interface StateInit {
  state: 'Init';
}

interface StateAwaitingSetup {
  state: 'AwaitingSetup';
  user: UserModel;
}

interface StateAwaitingCodeInput {
  state: 'AwaitingCodeInput';
  factorId: string;
  otpauthUri: string;
  user: UserModel;
}

interface StateAwaitingVerification {
  state: 'AwaitingVerification';
  factorId: string;
  otpauthUri: string;
  user: UserModel;
}

interface StateComplete {
  state: 'Complete';
  otpauthUri?: string;
}

interface StateError {
  state: 'Error';
  message: string;
}

type MfaSetupState =
  | StateInit
  | StateAwaitingSetup
  | StateAwaitingCodeInput
  | StateAwaitingVerification
  | StateComplete
  | StateError;

const InitState: StateInit = { state: 'Init' };

const mfaSetupReducer = (state: MfaSetupState, action: MfaAction): MfaSetupState => {
  switch (action.action) {
    case 'Init':
      return { state: 'Init' };
    case 'StartFactorSetup':
      return { state: 'AwaitingSetup', user: action.user };
    case 'CompleteFactorSetup':
      if (state.state !== 'AwaitingSetup') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingCodeInput',
        factorId: action.factorId,
        otpauthUri: action.otpauthUri,
        user: state.user,
      };
    case 'StartFactorVerification':
      if (state.state !== 'AwaitingCodeInput') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingVerification',
        factorId: state.factorId,
        otpauthUri: state.otpauthUri,
        user: state.user,
      };
    case 'FailFactorVerification':
      if (state.state !== 'AwaitingVerification') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingCodeInput',
        factorId: state.factorId,
        otpauthUri: state.otpauthUri,
        user: state.user,
      };
    case 'CompleteSetup':
      return {
        state: 'Complete',
        otpauthUri:
          state.state === 'AwaitingCodeInput' || state.state === 'AwaitingVerification' ? state.otpauthUri : undefined,
      };
    case 'Error':
      return { state: 'Error', message: action.message };
  }
};

interface SetupDeviceMFAProps {
  enabled?: boolean;
  fullWidth?: boolean;
  onSetupComplete?: () => void;
  onSuccess?: () => void;
  errorNotificationDisplay?: string;
  successNotificationDisplay?: string;
}

export const SetupDeviceMFA: React.FC<SetupDeviceMFAProps> = ({
  enabled = true,
  fullWidth = false,
  onSetupComplete,
  onSuccess,
  errorNotificationDisplay = 'page',
  successNotificationDisplay = errorNotificationDisplay,
}) => {
  const { currentUser: user, refetchCurrentUser, setMfaStatus } = useSessionStore();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const executeReCAPTCHA = useReCAPTCHA();
  const theme = useTheme();

  const [verificationCode, setVerificationCode] = useState<string>('');
  const [mfaSetupState, dispatch] = useReducer(mfaSetupReducer, InitState);
  const [showCopied, setShowCopied] = useState<boolean>(false);

  const setupMfaMutation = useMutation({
    mutationFn: async (userModel: UserModel) => {
      const reCaptchaToken = await executeReCAPTCHA(RecaptchaAction.MfaSetup);
      return UserRepository.setupMfa(reCaptchaToken, {
        factorName: userModel.email,
        factorType: FactorType.TOTP,
      });
    },
    onError: (error: any) => {
      dispatch({ action: 'Error', message: (error as Error).message });
    },
    onSuccess: (resp: { factorId?: string; otpauthUri?: string }) => {
      const { factorId, otpauthUri: otpauthUriResp } = resp;
      if (!factorId || !otpauthUriResp) {
        dispatch({ action: 'Error', message: 'An error occurred during setup. Please try again.' });
        return;
      }
      dispatch({ action: 'CompleteFactorSetup', factorId, otpauthUri: otpauthUriResp });
    },
  });

  const verifyMfaMutation = useMutation({
    mutationFn: async ({ code, factorId }: { code: string; factorId: string }) =>
      UserRepository.verifyMfa({
        code,
        factorId,
      }),
    onError: (error: any) => {
      let content = (error as Error)?.message ?? 'An error occurred';
      if (content === 'Incorrect login mfa code.') {
        content = 'Invalid verification code';
      }
      addDangerNotification({
        content,
        display: errorNotificationDisplay,
      });
      dispatch({ action: 'FailFactorVerification' });
      setVerificationCode('');
    },
    onSuccess: (resp: { mfaStatus: MFAStatus }) => {
      onSetupComplete?.();
      setMfaStatus(resp.mfaStatus);

      addSuccessNotification({
        content: 'Authenticator app enabled',
        display: successNotificationDisplay,
      });
      dispatch({ action: 'CompleteSetup' });
    },
  });

  const handleCopySetupCode = useCallback((event: React.MouseEvent, otpauthUriParam: string) => {
    event.preventDefault();
    setShowCopied(true);
    copyToClipboard(otpauthUriParam);
    setTimeout(() => {
      setShowCopied(false);
    }, 550);
  }, []);

  useEffect(() => {
    if (enabled && user && mfaSetupState.state === 'Init') {
      dispatch({ action: 'StartFactorSetup', user });
    }
  }, [enabled, user, mfaSetupState.state]);

  useEffect(() => {
    if (mfaSetupState.state === 'AwaitingSetup' && !setupMfaMutation.isPending) {
      setupMfaMutation.mutate(mfaSetupState.user);
    }
    if (mfaSetupState.state === 'AwaitingCodeInput' && verificationCode.length === 6) {
      dispatch({ action: 'StartFactorVerification' });
    }
    if (mfaSetupState.state === 'AwaitingVerification' && !verifyMfaMutation.isPending) {
      verifyMfaMutation.mutate({
        code: verificationCode,
        factorId: mfaSetupState.factorId,
      });
    }
  }, [mfaSetupState, verificationCode, setupMfaMutation, verifyMfaMutation]);

  useEffect(() => {
    if (mfaSetupState.state === 'Error') {
      addDangerNotification({
        content: mfaSetupState.message,
        display: errorNotificationDisplay,
      });
    }
  }, [mfaSetupState, addDangerNotification, errorNotificationDisplay]);

  useEffect(() => {
    if (mfaSetupState.state === 'Complete') {
      refetchCurrentUser().then(() => {
        onSuccess?.();
      });
    }
  }, [mfaSetupState, onSuccess, refetchCurrentUser]);

  const isLoading = mfaSetupState.state === 'Init' || mfaSetupState.state === 'AwaitingSetup';
  const isAwaitingCodeInput =
    mfaSetupState.state === 'AwaitingCodeInput' || mfaSetupState.state === 'AwaitingVerification';
  const isVerifying = mfaSetupState.state === 'AwaitingVerification';
  const otpauthUri =
    mfaSetupState.state === 'AwaitingCodeInput' ||
    mfaSetupState.state === 'AwaitingVerification' ||
    mfaSetupState.state === 'Complete'
      ? mfaSetupState.otpauthUri
      : undefined;
  const isComplete = mfaSetupState.state === 'Complete';

  const showLoading = isLoading || !(isAwaitingCodeInput || otpauthUri);

  if (fullWidth) {
    return (
      <StyledBox $fullWidth variant="secondary">
        <Loading centerSpinner isLoading={showLoading}>
          <Grid vertical gap={0}>
            <VerificationOverview $fullWidth>
              <p>
                Scan the QR code below with your preferred authenticator app. We recommend 1Password, Authy, or Google
                Authenticator.
              </p>
            </VerificationOverview>
            <MfaWrapper $fullWidth>
              <MfaQRCode $fullWidth>
                <QRCode fgColor={theme.foreground} bgColor="transparent" size={140} value={otpauthUri ?? ''} />
              </MfaQRCode>
              <MfaInput $fullWidth>
                <CodeInputDescription>Enter the code generated by your app</CodeInputDescription>
                <CodeInput
                  autoFocus
                  fullWidth
                  hideCaret
                  isComplete={isComplete}
                  isDisabled={isComplete}
                  isLoading={isVerifying}
                  placeholder="######"
                  showPlaceholderOnFocus
                  type="number"
                  value={verificationCode}
                  onChange={setVerificationCode}
                />
              </MfaInput>
            </MfaWrapper>
            <MfaSetupCode $fullWidth>
              <p>If your authenticator doesn't recognize the QR code, copy this setup code</p>
              <StyledTooltip $fullWidth content={'Copied!'} placement={'bottom'} zIndex={100} isOpen={showCopied}>
                <Button variant="secondary" size="tiny" onClick={(e) => handleCopySetupCode(e, otpauthUri ?? '')}>
                  Copy setup code
                </Button>
              </StyledTooltip>
            </MfaSetupCode>
          </Grid>
        </Loading>
      </StyledBox>
    );
  }

  return (
    <Loading isLoading={showLoading}>
      <Grid vertical>
        <VerificationOverview>
          <p>
            Scan the QR code below with your preferred authenticator app. We recommend 1Password, Authy, or Google
            Authenticator.
          </p>
        </VerificationOverview>
        <StyledBox variant="secondary">
          <MfaWrapper>
            <MfaQRCode>
              <QRCode fgColor={theme.foreground} bgColor="transparent" size={150} value={otpauthUri ?? ''} />
            </MfaQRCode>
            <MfaInput>
              <CodeInputDescription>Enter the code generated by your app</CodeInputDescription>
              <CodeInput
                autoFocus
                fullWidth
                hideCaret
                isComplete={isComplete}
                isDisabled={isComplete}
                isLoading={isVerifying}
                placeholder="######"
                showPlaceholderOnFocus
                type="number"
                value={verificationCode}
                onChange={setVerificationCode}
              />

              <MfaSetupCode>
                <p>If your authenticator doesn't recognize the QR code, copy this setup code</p>
                <StyledTooltip content={'Copied!'} placement={'bottom'} zIndex={100} isOpen={showCopied}>
                  <Button variant="secondary" size="tiny" onClick={(e) => handleCopySetupCode(e, otpauthUri ?? '')}>
                    Copy setup code
                  </Button>
                </StyledTooltip>
              </MfaSetupCode>
            </MfaInput>
          </MfaWrapper>
        </StyledBox>
      </Grid>
    </Loading>
  );
};
